import { useCallback } from "react";
import { useSWRConfig } from "swr";
import useSWRImmutable from "swr/immutable";
import { type AppSession, appSessionPath } from "~/services/apps";
import { cancelConversation, updateConversation } from "~/services/conversations";
import { projectConversationsPath } from "~/services/projects";
import { apiMutate } from "~/utils/apiClient";

export interface AppSessionState {
  session: AppSession | undefined;
  error: unknown;
  // Starts a fresh session for the app; the old one is kept, listed or not.
  newChat: () => Promise<void>;
  // Lists the session once the user talks to the agent in it.
  promote: () => Promise<void>;
}

/**
 * The caller's session with a published app, opened when the app page is. Off an
 * app page (`slug` undefined) it opens nothing. Immutable: the route creates on
 * first call, and a refetch on focus has nothing new to say.
 */
export function useAppSession(projectId: string, slug: string | undefined): AppSessionState {
  const path = slug ? appSessionPath(projectId, slug) : null;
  const { data, error, mutate } = useSWRImmutable(path, (url: string) =>
    apiMutate<AppSession>(url, "POST")
  );
  const { mutate: mutateCache } = useSWRConfig();

  const newChat = useCallback(async () => {
    if (!path) {
      return;
    }
    const fresh = await apiMutate<AppSession>(`${path}?fresh=true`, "POST");
    await mutate(fresh, { revalidate: false });
    // Nothing will read the abandoned run's answer, and a no-op cancel is a 204.
    if (data) {
      cancelConversation(data.id).catch((err) =>
        console.error("Failed to stop the replaced app session's run", err)
      );
    }
  }, [path, data, mutate]);

  const promote = useCallback(async () => {
    if (!data || data.listed) {
      return;
    }
    await mutate(
      async () => {
        await updateConversation(data.id, { listed: true });
        return { ...data, listed: true };
      },
      { optimisticData: { ...data, listed: true }, revalidate: false }
    );
    // Listed now, so it joins the project's session list.
    await mutateCache(projectConversationsPath(projectId));
  }, [data, mutate, mutateCache, projectId]);

  return { session: data, error, newChat, promote };
}
